import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import appointmentService from '../services/appointmentService';

const STATUS_COLORS = {
  realizadas:    '#10b981',
  canceladas:    '#ef4444',
  reprogramadas: '#f59e0b',
  programadas:   '#3b82f6',
  confirmadas:   '#6366f1',
};

const TYPE_LABELS = {
  firma:       'Firma',
  consulta:    'Consulta',
  entrega:     'Entrega',
  seguimiento: 'Seguimiento',
  interno:     'Interno',
};

const TYPE_COLORS = {
  firma:       '#1a1a2e',
  consulta:    '#3b82f6',
  entrega:     '#10b981',
  seguimiento: '#f59e0b',
  interno:     '#9ca3af',
};

const MONTHS = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'
];

export default function AgendaStats() {
  const navigate = useNavigate();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear]   = useState(now.getFullYear());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await appointmentService.getStats(month, year);
      setStats(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const years = Array.from({ length: 4 }, (_, i) => now.getFullYear() - i);

  // Calcular días del mes para la gráfica
  const daysInMonth = new Date(year, month, 0).getDate();
  const maxDay = stats ? Math.max(...Array.from({ length: daysInMonth }, (_, i) => stats.by_day[i + 1] || 0), 1) : 1;

  return (
    <div className="container-fluid py-3">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <div>
          <button className="btn btn-sm btn-outline-secondary mb-2" onClick={() => navigate('/agenda')}>
            ← Regresar
          </button>
          <h4 className="mb-0">Estadísticas de Agenda</h4>
          <small className="text-muted">{MONTHS[month - 1]} {year}</small>
        </div>

        {/* Filtros */}
        <div className="d-flex gap-2">
          <select className="form-control form-control-sm" value={month} onChange={e => setMonth(Number(e.target.value))} style={{ width: 130 }}>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select className="form-control form-control-sm" value={year} onChange={e => setYear(Number(e.target.value))} style={{ width: 90 }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {loading && <div className="text-center py-5"><div className="spinner-border text-primary" /></div>}

      {!loading && stats && (
        <>
          {/* Tarjetas resumen */}
          <div className="row mb-4">
            {[
              { label: 'Total de citas',    value: stats.total,          color: '#1a1a2e', bg: '#f0f0f5' },
              { label: 'Realizadas',        value: stats.realizadas,     color: STATUS_COLORS.realizadas,    bg: '#d1fae5' },
              { label: 'Canceladas',        value: stats.canceladas,     color: STATUS_COLORS.canceladas,    bg: '#fee2e2' },
              { label: 'Reprogramadas',     value: stats.reprogramadas,  color: STATUS_COLORS.reprogramadas, bg: '#fef3c7' },
              { label: 'Tasa de completado', value: `${stats.tasa_completado}%`, color: '#10b981', bg: '#d1fae5' },
            ].map((card, i) => (
              <div key={i} className="col-6 col-md-2 mb-3">
                <div className="card h-100" style={{ borderTop: `3px solid ${card.color}` }}>
                  <div className="card-body text-center py-3">
                    <div style={{ fontSize: 28, fontWeight: 700, color: card.color }}>{card.value}</div>
                    <small className="text-muted">{card.label}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="row mb-4">
            {/* Gráfica por día */}
            <div className="col-12 col-md-8 mb-3">
              <div className="card h-100">
                <div className="card-header"><strong>Citas por día</strong></div>
                <div className="card-body">
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 120, overflowX: 'auto' }}>
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const day   = i + 1;
                      const total = stats.by_day[day] || 0;
                      const pct   = Math.round((total / maxDay) * 100);
                      return (
                        <div key={day} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 18 }}>
                          {total > 0 && (
                            <div style={{ fontSize: 9, color: '#555', marginBottom: 2 }}>{total}</div>
                          )}
                          <div style={{
                            width: '100%',
                            height: pct > 0 ? `${pct}%` : 3,
                            background: pct > 0 ? '#1a1a2e' : '#e5e7eb',
                            borderRadius: '3px 3px 0 0',
                            minHeight: 3,
                          }} />
                          <div style={{ fontSize: 8, color: '#9ca3af', marginTop: 3 }}>{day}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Por tipo */}
            <div className="col-12 col-md-4 mb-3">
              <div className="card h-100">
                <div className="card-header"><strong>Por tipo de cita</strong></div>
                <div className="card-body">
                  {Object.entries(TYPE_LABELS).map(([key, label]) => {
                    const total = stats.by_type[key] || 0;
                    const pct   = stats.total > 0 ? Math.round((total / stats.total) * 100) : 0;
                    return (
                      <div key={key} className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <small>{label}</small>
                          <small><strong>{total}</strong> ({pct}%)</small>
                        </div>
                        <div className="progress" style={{ height: 6 }}>
                          <div className="progress-bar" style={{ width: `${pct}%`, backgroundColor: TYPE_COLORS[key] }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Por status */}
            <div className="col-12 col-md-6 mb-3">
              <div className="card h-100">
                <div className="card-header"><strong>Por status</strong></div>
                <div className="card-body">
                  {[
                    { key: 'realizadas',    label: 'Realizadas' },
                    { key: 'confirmadas',   label: 'Confirmadas' },
                    { key: 'programadas',   label: 'Programadas' },
                    { key: 'reprogramadas', label: 'Reprogramadas' },
                    { key: 'canceladas',    label: 'Canceladas' },
                  ].map(({ key, label }) => {
                    const total = stats[key] || 0;
                    const pct   = stats.total > 0 ? Math.round((total / stats.total) * 100) : 0;
                    return (
                      <div key={key} className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <small>{label}</small>
                          <small><strong>{total}</strong> ({pct}%)</small>
                        </div>
                        <div className="progress" style={{ height: 6 }}>
                          <div className="progress-bar" style={{ width: `${pct}%`, backgroundColor: STATUS_COLORS[key] }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Por usuario */}
            <div className="col-12 col-md-6 mb-3">
              <div className="card h-100">
                <div className="card-header"><strong>Por responsable</strong></div>
                <div className="card-body p-0">
                  {stats.by_user.length === 0 ? (
                    <div className="text-center text-muted py-4">Sin datos</div>
                  ) : (
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Usuario</th>
                          <th className="text-right">Citas</th>
                          <th className="text-right">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.by_user
                          .sort((a, b) => b.total - a.total)
                          .map((u, i) => {
                            const pct = stats.total > 0 ? Math.round((u.total / stats.total) * 100) : 0;
                            return (
                              <tr key={i}>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div style={{
                                      width: 28, height: 28, borderRadius: '50%',
                                      background: '#EEF0FB', color: '#5066E1',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontWeight: 700, fontSize: 12, flexShrink: 0,
                                    }}>
                                      {u.user.charAt(0).toUpperCase()}
                                    </div>
                                    <span style={{ fontSize: 13 }}>{u.user}</span>
                                  </div>
                                </td>
                                <td className="text-right"><strong>{u.total}</strong></td>
                                <td className="text-right"><span className="badge badge-secondary">{pct}%</span></td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>

          {stats.total === 0 && (
            <div className="text-center text-muted py-5">
              No hay citas registradas en {MONTHS[month - 1]} {year}
            </div>
          )}
        </>
      )}
    </div>
  );
}